// ============================================================
// LoopRoleInference.js — Determines the structural role of a loop
//
// Given audio analysis + genre classification, infers what section
// of a track this loop best represents (drop, intro, breakdown, etc.)
// and what elements are present (kick, bass, synth, vocal, etc.)
//
// This is the bridge between "what does this loop sound like" and
// "where does it go in an arrangement."
// ============================================================

/**
 * Possible structural roles a loop can play in an arrangement.
 * Each role maps to specific sections in the final arrangement.
 */
export const LOOP_ROLES = {
  MAIN_DROP: 'main-drop',
  BUILDUP: 'buildup',
  BREAKDOWN: 'breakdown',
  INTRO: 'intro',
  BASS_LOOP: 'bass-loop',
  DRUM_BREAK: 'drum-break',
  MELODIC_HOOK: 'melodic-hook',
  PAD_ATMOSPHERE: 'pad-atmosphere',
  FULL_MIX: 'full-mix',
  VOCAL_HOOK: 'vocal-hook',
};

/**
 * Element tags that describe what's present in the loop.
 * Used by the arrangement generator to decide what to add/subtract
 * when building surrounding sections.
 */
export const ELEMENTS = {
  KICK: 'kick',
  SNARE: 'snare',
  HIHATS: 'hi-hats',
  PERCUSSION: 'percussion',
  BASS: 'bass',
  SUB_BASS: 'sub-bass',
  LEAD_SYNTH: 'lead-synth',
  PAD: 'pad',
  VOCAL: 'vocal',
  FX: 'fx',
  GUITAR: 'guitar',
  STRINGS: 'strings',
};

/**
 * Infer the structural role and element composition of a loop.
 *
 * Uses a scoring approach: each role has conditions based on energy
 * profile, and the highest-scoring role wins. Also tags likely
 * elements present based on spectral characteristics.
 *
 * @param {Object} analysis - Result from analyzeLoop()
 * @param {Object} genreResult - Result from classifyGenre()
 * @returns {{
 *   role: string,
 *   roleLabel: string,
 *   roleDescription: string,
 *   confidence: number,
 *   elements: string[],
 *   alternativeRoles: Array<{role: string, label: string, confidence: number}>
 * }}
 */
export function inferLoopRole(analysis, genreResult) {
  const e = analysis.energy;
  const genre = genreResult.primary.name;
  const family = genreResult.family;

  // --- Score each possible role ---
  const roleScores = [];

  // MAIN DROP: high energy, strong bass, percussive elements
  // This is the "main idea" of the track — the part with maximum impact
  roleScores.push({
    role: LOOP_ROLES.MAIN_DROP,
    label: 'Main Drop',
    description: 'The high-energy core of your track. Maximum impact — kick, bass, and lead all firing.',
    score: scoreMainDrop(e, family)
  });

  // BUILDUP: rising energy, moderate-high mid/treble, less bass
  roleScores.push({
    role: LOOP_ROLES.BUILDUP,
    label: 'Buildup',
    description: 'Tension-building section. Rising energy with filtered elements preparing for the drop.',
    score: scoreBuildup(e, family)
  });

  // BREAKDOWN: melodic focus, lower percussive, moderate energy
  roleScores.push({
    role: LOOP_ROLES.BREAKDOWN,
    label: 'Breakdown',
    description: 'Melodic breather between high-energy sections. Kick usually drops out.',
    score: scoreBreakdown(e, family)
  });

  // INTRO: low-moderate energy, building atmosphere
  roleScores.push({
    role: LOOP_ROLES.INTRO,
    label: 'Intro',
    description: 'Opening section that establishes mood. Stripped-back elements that gradually build.',
    score: scoreIntro(e, family)
  });

  // BASS LOOP: dominant bass, minimal mid/treble
  roleScores.push({
    role: LOOP_ROLES.BASS_LOOP,
    label: 'Bass Loop',
    description: 'Pure bass/sub element. The low-end foundation that other elements layer over.',
    score: scoreBassLoop(e, family)
  });

  // DRUM BREAK: highly percussive, minimal melodic
  roleScores.push({
    role: LOOP_ROLES.DRUM_BREAK,
    label: 'Drum Break',
    description: 'Percussion-focused loop. Drives the rhythm — everything else builds on top.',
    score: scoreDrumBreak(e, family)
  });

  // MELODIC HOOK: high melodic score, mid/treble presence
  roleScores.push({
    role: LOOP_ROLES.MELODIC_HOOK,
    label: 'Melodic Hook',
    description: 'The memorable melody that carries the emotional weight of the track.',
    score: scoreMelodicHook(e, family)
  });

  // PAD/ATMOSPHERE: low percussive, sustained, sparse
  roleScores.push({
    role: LOOP_ROLES.PAD_ATMOSPHERE,
    label: 'Pad / Atmosphere',
    description: 'Ambient texture that fills space. Works as a bed layer under other elements.',
    score: scorePadAtmosphere(e, family)
  });

  // FULL MIX: everything present, balanced, high overall
  roleScores.push({
    role: LOOP_ROLES.FULL_MIX,
    label: 'Full Mix',
    description: 'A complete section with all elements. The arrangement will build toward and away from this.',
    score: scoreFullMix(e, family)
  });

  // Sort by score
  roleScores.sort((a, b) => b.score - a.score);

  const best = roleScores[0];
  const alternatives = roleScores
    .slice(1)
    .filter(r => r.score > 0.3)
    .slice(0, 3)
    .map(r => ({ role: r.role, label: r.label, confidence: Math.round(r.score * 100) / 100 }));

  // --- Infer elements present ---
  const elements = inferElements(e, family);

  return {
    role: best.role,
    roleLabel: best.label,
    roleDescription: best.description,
    confidence: Math.round(best.score * 100) / 100,
    elements,
    alternativeRoles: alternatives
  };
}

// --- Role scoring functions ---

function scoreMainDrop(e, family) {
  let score = 0;
  // High overall energy
  score += e.overall > 0.6 ? 0.3 : e.overall * 0.5;
  // Strong bass
  score += e.bass > 0.5 ? 0.25 : e.bass * 0.5;
  // Some percussive element
  score += e.percussive > 0.25 ? 0.2 : e.percussive * 0.8;
  // Not purely melodic — drops have rhythm
  score += e.percussive > e.melodic * 0.5 ? 0.15 : 0;
  // Density is dense or bass-heavy
  if (e.density === 'dense' || e.density === 'bass-heavy') score += 0.1;
  return Math.min(1, score);
}

function scoreBuildup(e, family) {
  let score = 0;
  // Moderate energy (not max, not min)
  if (e.overall > 0.3 && e.overall < 0.8) score += 0.25;
  // Mid/treble emphasis over bass (filtered low end)
  if (e.mid > e.bass * 0.8) score += 0.2;
  if (e.treble > 0.2) score += 0.15;
  // Some percussive drive
  if (e.percussive > 0.3 && e.percussive < 0.7) score += 0.15;
  // Not bass-heavy density
  if (e.density === 'bright' || e.density === 'balanced') score += 0.15;
  // Rising character (high mid + treble relative to bass)
  if ((e.mid + e.treble) > e.bass) score += 0.1;
  return Math.min(1, score);
}

function scoreBreakdown(e, family) {
  let score = 0;
  // Melodic > percussive
  if (e.melodic > e.percussive) score += 0.3;
  // Lower percussive (kick usually drops out)
  if (e.percussive < 0.35) score += 0.2;
  // Mid-range presence (melodic content lives here)
  if (e.mid > 0.25) score += 0.15;
  // Moderate overall (not peak energy)
  if (e.overall < 0.7 && e.overall > 0.2) score += 0.15;
  // Balanced or melodic density
  if (e.density === 'melodic' || e.density === 'balanced') score += 0.15;
  // Lower bass (kick removed)
  if (e.bass < 0.5) score += 0.1;
  return Math.min(1, score);
}

function scoreIntro(e, family) {
  let score = 0;
  // Low overall energy
  if (e.overall < 0.4) score += 0.3;
  else if (e.overall < 0.6) score += 0.15;
  // Sparse or balanced density
  if (e.density === 'sparse' || e.density === 'balanced') score += 0.2;
  // Low percussive (intros are often kick-less or minimal)
  if (e.percussive < 0.3) score += 0.2;
  // Some atmosphere/pad character
  if (e.melodic > 0.3) score += 0.15;
  // Not bass-heavy (full bass comes later)
  if (e.bass < 0.5) score += 0.15;
  return Math.min(1, score);
}

function scoreBassLoop(e, family) {
  let score = 0;
  // Dominant bass, minimal everything else
  if (e.bass > 0.7 && e.mid < 0.2 && e.treble < 0.1) score += 0.5;
  else if (e.bass > 0.6 && e.mid < 0.3) score += 0.3;
  // Bass-heavy density
  if (e.density === 'bass-heavy') score += 0.2;
  // Low percussive (pure bass, not a full beat)
  if (e.percussive < 0.2) score += 0.2;
  // Low melodic (sub bass, not a melody)
  if (e.melodic < 0.3) score += 0.1;
  return Math.min(1, score);
}

function scoreDrumBreak(e, family) {
  let score = 0;
  // High percussive, low melodic
  if (e.percussive > 0.5 && e.melodic < 0.3) score += 0.4;
  else if (e.percussive > 0.4) score += 0.2;
  // Percussive density
  if (e.density === 'percussive') score += 0.2;
  // Moderate bass (kick present but no sub bass)
  if (e.bass > 0.3 && e.bass < 0.7) score += 0.15;
  // Some treble (hi-hats, cymbals)
  if (e.treble > 0.15) score += 0.15;
  // Low melodic content
  if (e.melodic < 0.2) score += 0.1;
  return Math.min(1, score);
}

function scoreMelodicHook(e, family) {
  let score = 0;
  // High melodic score
  if (e.melodic > 0.5) score += 0.35;
  else if (e.melodic > 0.3) score += 0.15;
  // Mid presence (melodies live in mids)
  if (e.mid > 0.3) score += 0.2;
  // Some treble (harmonics, brightness)
  if (e.treble > 0.15) score += 0.15;
  // Lower bass (not a bass-driven loop)
  if (e.bass < 0.5) score += 0.15;
  // Melodic density
  if (e.density === 'melodic' || e.density === 'bright') score += 0.15;
  return Math.min(1, score);
}

function scorePadAtmosphere(e, family) {
  let score = 0;
  // Very low percussive (sustained, not rhythmic)
  if (e.percussive < 0.15) score += 0.35;
  else if (e.percussive < 0.25) score += 0.2;
  // Low overall energy (ambient)
  if (e.overall < 0.3) score += 0.2;
  // Sparse density
  if (e.density === 'sparse') score += 0.2;
  // Some mid/treble (pads have warmth/shimmer)
  if (e.mid > 0.2 || e.treble > 0.15) score += 0.15;
  // Melodic character
  if (e.melodic > 0.3) score += 0.1;
  return Math.min(1, score);
}

function scoreFullMix(e, family) {
  let score = 0;
  // High overall energy
  if (e.overall > 0.7) score += 0.25;
  // All bands present (balanced mix)
  if (e.bass > 0.3 && e.mid > 0.25 && e.treble > 0.1) score += 0.25;
  // Both percussive and melodic elements
  if (e.percussive > 0.25 && e.melodic > 0.25) score += 0.2;
  // Dense or balanced
  if (e.density === 'dense' || e.density === 'balanced') score += 0.15;
  // Not extremely lopsided in any direction
  const spread = Math.abs(e.bass - e.mid) + Math.abs(e.mid - e.treble);
  if (spread < 0.8) score += 0.15;
  return Math.min(1, score);
}

// --- Element inference ---

/**
 * Infer which musical elements are likely present in the loop.
 * Uses energy band analysis and genre context.
 */
function inferElements(e, family) {
  const elements = [];

  // Kick: present if bass > 0.4 and percussive > 0.2
  if (e.bass > 0.4 && e.percussive > 0.2) elements.push(ELEMENTS.KICK);

  // Snare: percussive + mid presence
  if (e.percussive > 0.3 && e.mid > 0.2) elements.push(ELEMENTS.SNARE);

  // Hi-hats: treble + percussive
  if (e.treble > 0.1 && e.percussive > 0.25) elements.push(ELEMENTS.HIHATS);

  // Bass/Sub: strong low end
  if (e.bass > 0.6) {
    elements.push(ELEMENTS.SUB_BASS);
    elements.push(ELEMENTS.BASS);
  } else if (e.bass > 0.35) {
    elements.push(ELEMENTS.BASS);
  }

  // Lead synth: melodic + mid/treble presence
  if (e.melodic > 0.4 && e.mid > 0.3 && family === 'electronic') {
    elements.push(ELEMENTS.LEAD_SYNTH);
  }

  // Pad: melodic + low percussive + sparse/balanced
  if (e.melodic > 0.3 && e.percussive < 0.25 && (e.density === 'sparse' || e.density === 'balanced')) {
    elements.push(ELEMENTS.PAD);
  }

  // Guitar: mid-heavy + rock family
  if (family === 'rock' && e.mid > 0.4) {
    elements.push(ELEMENTS.GUITAR);
  }

  // General percussion: additional percussive beyond kick/snare
  if (e.percussive > 0.4 && e.treble > 0.15) {
    elements.push(ELEMENTS.PERCUSSION);
  }

  // FX: high treble relative to mid, non-percussive
  if (e.treble > 0.3 && e.percussive < 0.3) {
    elements.push(ELEMENTS.FX);
  }

  return elements;
}

/**
 * Get a human-readable summary of the loop analysis.
 * Useful for the UI to display a quick description.
 *
 * @param {Object} roleResult - Result from inferLoopRole()
 * @param {Object} genreResult - Result from classifyGenre()
 * @returns {string}
 */
export function getLoopSummary(roleResult, genreResult) {
  const genre = genreResult.primary.name;
  const role = roleResult.roleLabel;
  const elements = roleResult.elements;

  const elementStr = elements.length > 0
    ? elements.slice(0, 4).join(', ')
    : 'minimal elements';

  return `${genre} ${role.toLowerCase()} with ${elementStr}`;
}
